import { formatDistanceToNow } from 'date-fns';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import type { ProvisioningJob } from '@/services/provisioning';
import { ProvisioningStatusBadge } from './provisioning-status-badge';

interface Props {
    jobs: ProvisioningJob[];
    selectedJobId?: string;
    onSelect: (jobId: string) => void;
}

const duration = (job: ProvisioningJob): string => {
    if (!job.startedAt) return '—';
    const end = job.finishedAt ?? job.verifiedAt;
    if (!end) return 'running';
    const ms = new Date(end).getTime() - new Date(job.startedAt).getTime();
    const mins = Math.floor(ms / 60000);
    const secs = Math.round((ms % 60000) / 1000);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
};

export function ProvisioningJobHistory({ jobs, selectedJobId, onSelect }: Props) {
    return (
        <div className="overflow-x-auto rounded-md border border-slate-200 dark:border-slate-800">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-16">#</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Started</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Triggered by</TableHead>
                        <TableHead>Outcome</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {jobs.map((job) => (
                        <TableRow
                            key={job.id}
                            onClick={() => onSelect(job.id)}
                            className={
                                job.id === selectedJobId
                                    ? 'cursor-pointer bg-slate-50 dark:bg-slate-900'
                                    : 'cursor-pointer'
                            }
                        >
                            <TableCell className="font-medium">{job.attempt}</TableCell>
                            <TableCell>
                                <ProvisioningStatusBadge status={job.status} />
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                                {formatDistanceToNow(new Date(job.queuedAt), { addSuffix: true })}
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                                {duration(job)}
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">
                                {job.triggeredBy?.email ?? '—'}
                            </TableCell>
                            <TableCell className="max-w-xs truncate text-sm text-slate-500">
                                {job.errorCode ? `${job.errorCode}: ` : ''}
                                {job.errorMessage ??
                                    (job.stats
                                        ? `ok=${job.stats.ok} changed=${job.stats.changed}`
                                        : '—')}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
